/**
 * SmartImprove API client — talks to /api/smartimprove/* with credentials
 * so the admin/consultant JWT cookie flows through. Mirrors the pattern in
 * adminApi.js so the consultant workspace keeps a consistent feel.
 */
import axios from "axios";

const API = `${process.env.REACT_APP_BACKEND_URL}/api/smartimprove`;
const ax = axios.create({ baseURL: API, withCredentials: true });

// Engagements
export const listEngagements = () => ax.get("/engagements").then((r) => r.data);
export const getEngagement = (id) => ax.get(`/engagements/${id}`).then((r) => r.data);
export const createEngagement = (d) => ax.post("/engagements", d).then((r) => r.data);
export const duplicateEngagement = (id) => ax.post(`/engagements/${id}/duplicate`).then((r) => r.data);
export const updateEngagement = (id, d) => ax.put(`/engagements/${id}`, d).then((r) => r.data);
export const archiveEngagement = (id, payload) =>
  ax.post(`/engagements/${id}/archive`, payload).then((r) => r.data);
export const restoreEngagement = (id) =>
  ax.post(`/engagements/${id}/restore`).then((r) => r.data);

// Public intake — unauthenticated POST used by the `/improve/start` landing
// page so prospects can submit a SmartImprove engagement without an admin
// account. Mirrors SmartEvaluate's public evaluation submission. The full
// engagement payload is intentionally restricted server-side; admin-only
// fields (visibility, role, status overrides) cannot be set via this route.
export const submitPublicIntake = (d) =>
  ax.post("/intake", d).then((r) => r.data);

// Operational areas (shared vocabulary across both modules)
export const listOperationalAreas = () => ax.get("/operational-areas").then((r) => r.data);

// Admin-managed sub-processes. Optionally filtered by `process` (operational
// area key) so the Edit Finding modal can scope the dropdown after the user
// picks a Business Process.
export const listSubProcesses = (process) =>
  ax
    .get("/sub-processes", { params: process ? { process } : {} })
    .then((r) => r.data);

// Issues / observations / KPIs (append-only in Phase 1)
export const addIssue = (id, d) => ax.post(`/engagements/${id}/issues`, d).then((r) => r.data);
export const listIssues = (id) => ax.get(`/engagements/${id}/issues`).then((r) => r.data);
export const updateIssue = (issueId, d) => ax.put(`/issues/${issueId}`, d).then((r) => r.data);

export const addObservation = (id, d) =>
  ax.post(`/engagements/${id}/observations`, d).then((r) => r.data);
export const listObservations = (id) =>
  ax.get(`/engagements/${id}/observations`).then((r) => r.data);

export const addKpi = (id, d) => ax.post(`/engagements/${id}/kpis`, d).then((r) => r.data);
export const listKpis = (id) => ax.get(`/engagements/${id}/kpis`).then((r) => r.data);

// Customer cross-module summary
export const getCustomerSummary = (customerId) =>
  ax.get(`/customers/${customerId}/summary`).then((r) => r.data);


// Phase 6.5A — sites (warehouses) + enhancement groups
export const listSites = (id) =>
  ax.get(`/engagements/${id}/sites`).then((r) => r.data);
export const addSite = (id, d) =>
  ax.post(`/engagements/${id}/sites`, d).then((r) => r.data);
export const updateSite = (id, siteId, d) =>
  ax.patch(`/engagements/${id}/sites/${siteId}`, d).then((r) => r.data);
export const deleteSite = (id, siteId) =>
  ax.delete(`/engagements/${id}/sites/${siteId}`).then((r) => r.data);

export const listGroups = (id) =>
  ax.get(`/engagements/${id}/groups`).then((r) => r.data);
export const addGroup = (id, d) =>
  ax.post(`/engagements/${id}/groups`, d).then((r) => r.data);
export const updateGroup = (id, groupId, d) =>
  ax.patch(`/engagements/${id}/groups/${groupId}`, d).then((r) => r.data);
export const deleteGroup = (id, groupId) =>
  ax.delete(`/engagements/${id}/groups/${groupId}`).then((r) => r.data);
